import * as React from "react";
import { ClinicActionButton } from "fe-web-components/clinic/clinicActionButton";
import { styled } from "@mui/material";
import { ClinicColors } from "fe-web-components/styles/clinicTheme";
import HIPPALogo from "../../../assets/clinic/HIPAALogo.png";
import GDPRLogo from "../../../assets/clinic/GDPR-badge.png";
import MainImage from "../../../assets/clinic/start_image1.png";
import { ReactComponent as LoginIcon } from "../../../assets/clinic/start_loginIcon.svg";
import { ReactComponent as CreateIcon } from "../../../assets/clinic/start_createIcon.svg";
import { ReactComponent as GetStartedIcon } from "../../../assets/clinic/start_getStartedIcon.svg";
import { ReactComponent as ArrowRight } from "../../../assets/clinic/arrowRight.svg";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";

//region [[ Styles ]]

const ClinicStartView = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  minHeight: "100%",
  fontSize: 20,
  color: ClinicColors.titles,
  [theme.breakpoints.down("xs")]: {
    marginRight: -15,
    marginLeft: -15,
    fontSize: 18,
  },
}));
const ClinicStartTop = styled("div")(({ theme }) => ({
  width: "92%",
  display: "flex",
  gap: 20,
  alignSelf: "center",
  maxWidth: 1300,
  flex: "0 1 auto",
  padding: "10px 20px 50px 20px",
  [theme.breakpoints.down("xs")]: {
    flexDirection: "column",
    paddingTop: 10,
    gap: 16,
    paddingBottom: 30,
    width: "100%",
  },
}));
const ClinicStartBottom = styled("div")(({ theme }) => ({
  textAlign: "center",
  alignItems: "center",
  display: "flex",
  flexDirection: "column",
  flex: "1",
  gap: 20,
  padding: "80px 20px",
  background: "#FFFFFF",
  fontWeight: "bold",
  [theme.breakpoints.down("xs")]: {
    padding: "40px 20px",
  },
}));
const ClinicStartText = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "start",
  justifyContent: "space-between",
  "& h1": {
    fontWeight: 900,
    margin: 0,
    marginBottom: 16,
  },
  "& ul": {
    marginTop: 20,
    listStyle: "none",
    paddingLeft: 0,
  },
  "& li": {
    paddingLeft: 33,
    marginBottom: 10,
    position: "relative",
  },
  "& li:before": {
    display: "block",
    content: '""',
    position: "absolute",
    left: 0,
    top: 3,
    width: 23,
    height: 23,
    backgroundColor: ClinicColors.primary,
    background: `url('data:image/svg+xml,<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="check-circle" class="svg-inline--fa fa-check-circle fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 11"><path d="M12.2499 1.85828L4.87077 9.23744L1.5166 5.88328" stroke="white" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>') no-repeat center center/14px`,
    borderRadius: "50%",
    color: "#ffffff",
  },
  [theme.breakpoints.down("md")]: {
    flex: "1 0 60%",
  },
  [theme.breakpoints.down("xs")]: {
    "& img.main-start-image": {
      height: 180,
    },
  },
  "@media (min-height: 850px)": {
    "& .upper-button": {
      display: "none",
    },
  },
}));
const ClinicStartStepsList = styled("div")(({ theme }) => ({
  justifyContent: "center",
  display: "flex",
  alignItems: "center",
  gap: 30,
  fontSize: 22,
  lineHeight: 1.2,
  [theme.breakpoints.down("sm")]: {
    gap: 20,
    fontSize: 20,
  },
  [theme.breakpoints.down("xs")]: {
    gap: 8,
    fontSize: 18,
  },
}));
const ClinicStartStep = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 16,
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    gap: 6,
  },
}));
const ClinicStartLogos = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 16,
  "& img": {
    height: 65,
  },
  [theme.breakpoints.down("xs")]: {
    alignSelf: "center",
    paddingTop: 20,
  },
}));
//endregion [[ Styles ]]

//region [[ Props ]]

export interface ClinicStartSettingsProps {
  gotoSignUp: () => void;
}

export const ClinicStart = ({ ...props }: ClinicStartSettingsProps) => {
  const handleStart = () => {
    evt({
      category: CATEGORY.CLINIC_MANAGEMENT,
      action: ACTION.CLINIC_STARTED_REGISTRATION,
    });
    props.gotoSignUp();
  };

  return (
    <ClinicStartView>
      <ClinicStartTop>
        <ClinicStartText>
          <div>
            <h1>Welcome to Kahun</h1>
            Create your Kahun clinic now and see how AI-powered clinical
            assessments help you optimize patient visits.
            <ul>
              <li>No credit card</li>
              <li>No integration</li>
              <li>Quick setup</li>
            </ul>
            <ClinicActionButton
              onClick={handleStart}
              label="Let's start"
              className="upper-button"
            />
          </div>
          <ClinicStartLogos>
            <a
              href="https://www.hhs.gov/hipaa/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={HIPPALogo} alt="Fortified health HIPPA compliance" />
            </a>
            <a
              href="https://gdpr.eu/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={GDPRLogo} alt="GDPR badge" />
            </a>
          </ClinicStartLogos>
        </ClinicStartText>
        <img
          src={MainImage}
          alt="Kahun Clinic Management"
          className={"main-start-image"}
        />
      </ClinicStartTop>
      <ClinicStartBottom>
        <div>Simply follow these steps & start experiencing Kahun:</div>
        <ClinicStartStepsList>
          <ClinicStartStep>
            <LoginIcon />
            Sign up
          </ClinicStartStep>
          <ArrowRight />
          <ClinicStartStep>
            <CreateIcon />
            Create your clinic
          </ClinicStartStep>
          <ArrowRight />
          <ClinicStartStep>
            <GetStartedIcon />
            Get started
          </ClinicStartStep>
        </ClinicStartStepsList>
        <ClinicActionButton
          onClick={handleStart}
          label="Let's start"
          style={{ fontSize: 20, fontWeight: "bold" }}
        />
      </ClinicStartBottom>
    </ClinicStartView>
  );
};
